import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import * as Sentry from '@sentry/vue';
import encryptAES256GCM from '@utils/encryptAES';
import decryptAES256GCM from '@utils/decryptAES';
import generateVersionHash from '@utils/generateVersionHash';
import decryptUserData from '@utils/decryptUserData';
import CustomEncryption from '../lib/CustomEncryption';
import { btoa, atob, isEmpty, date, showAlert} from '../lib/helpers';
import { postApi } from '../lib/fetcher';
import {
  dataIntervalExport,
  dataIntervalExportCh,
  dataWaitFileGenerate,
  dataIntervalCheckFile,
  dataMaxCheckFileInterval,
  dataMaxExportStatusInterval,
  dataExtraString3,
} from './params';
import users from './users';
import rbac from './rbac/rbac';
import register from './onboarding/register';
// eslint-disable-next-line import/no-cycle
import daftarTugas from './daftar-tugas/daftarTugas';
import client from './client/client';
import inputer from './inputer/inputer';
import settingFee from './setting-fee/settingFee';
import master from './master/master';
import rekening from './onboarding/rekening';
import report from './report/report';
import reportEdc from './report-edc/reportEdc';
import trackingPengajuan from './trackingPengajuan';
import inputerTrackingPengajuan from './inputerTrackingPengajuan';
import dashboard from './dashboard';
import broadcast from './broadcast/broadcast';
import changePIC from './change-pic';
import resendEmail from './resend-email/resendEmail';
import merchantManagement from './merchant-management';
import maintenanceSettlement from './maintenance-settlement/maintenanceSettlement';
import internalRefund from './refund-internal';
import refundInternal from './refund-internal/refundInternal';
import nnsManagement from './nns-management/nnsManagement';
import settlementSetting from './settlement-setting/settlementSetting';
import feeManagement from './fee-management/feeManagement';
import reportWalletTransactionStore from './report-wallet-transaction/reportWalletTransaction';
import dashboardDetail from './dashboard-detail/dashboardDetail';
import dashboardProfiling from './dashboard-profiling/dashboardProfiling';
import fileSettlement from './file-settlement/fileSettlement';
import monitoringMerchant from './monitoring-merchant/monitoringMerchant';
import reportBpjstk from './report-bpjstk/reportBpjstk';
import promo from './promo';
import reportPromo from './report-promo/reportPromo';
import userMerchant from './user-management-merchant/userMerchant';
import forceActiveMerchant from './force-active-merchant/forceActiveMerchant';
import reportCpmTransaction from './report-cpm/reportCpmTransaction';
import stockMid from './stock-mid/stockMid';
import mid from './mid/mid';
import merchantTools from './merchant-tools';
import insertTransactionOnUs from './insert-transaction-on-us';
import insertTransactionOffUs from './insert-transaction';
import reconciliation from './reconciliation/reconciliation';
import systemLog from './system-log';
import merchantCallbackManagement from './merchant-callback-management';
import { setupZohoChat, removeZohoChat } from '../plugins/zohoChat';
import { generateVisitorId } from '../lib/utils/fingerprint';
import shared from './shared';
import masterData from './master-data';


Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: '',
    authenticated: false,
    expired: 0,
    isExpired: false,
    key: '',
    loading: false,
    isMenuLoading: false,
    menus: [],
    logoutLoading: false,
    snackbar: false,
    snackbarIcon: '',
    snackbarColor: '',
    snackbarMessage: '',
    absoluteBar: false,
    absoluteBarText: '',
    minimized: false,
    breadcrumbs: [],
    item: null,
    isExport: false,
    intervalCheckFile: null,
    intervalExport: null,
    countDoneExport: 0,
    countSameExport: 0,
    checkStatusFileCount: 0,
    exportStatusRequest: null,
    checkFileRequest: null,
    notifications: [],
    requestData: null,
    exportId: null,
    countDataExport: null,
    expiredOtp: {},
    loginCredential: {},
    userCredential: {},
    changeEmailToken: null,
    changeEmailAccessKey: null,
    changeEmailUserId: null,
    changeEmailUserIdPlain: null,
    changeEmailAesKey: null,
    setPasswordToken: null,
    setPasswordAccessKey: null,
    setPasswordUserId: null,
    setPasswordUserIdPlain: null,
    setPasswordAesKey: null,
    setPasswordEmail: null,
    setPasswordEmailPlain: null,
    dormantReactivatedToken: null,
    dormantReactivatedAccessKey: null,
    dormantReactivatedUserId: null,
    dormantReactivatedUserIdPlain: null,
    dormantReactivatedAesKey: null,
  },

  mutations: {
    setExpiredOtp (state, expiredTime) {
      state.expiredOtp = expiredTime;
    },
    setLoginCredential (state, loginCredential) {
      state.loginCredential = loginCredential;
    },
    setUserCredential (state, userCredential) {
      state.userCredential = userCredential;
    },
    setUser(state, user) {
      state.user = user;
    },
    setAuth(state, authenticated) {
      state.authenticated = authenticated;
    },
    setKey(state, key) {
      state.key = key;
    },
    setExpired(state, expired) {
      state.expired = expired;
    },
    setIsExpired(state, isExpired) {
      state.isExpired = isExpired;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setLogoutLoading(state, logoutLoading) {
      state.logoutLoading = logoutLoading;
    },
    setItem(state, { payload }) {
      state.item = payload.data.data;
    },
    setMenus(state, menu) {
      state.menus = menu;
    },
    setMenuLoading(state, isMenuLoading) {
      state.isMenuLoading = isMenuLoading;
    },
    setSnackbar(state, { status, message, icon, color }) {
      state.snackbar = status;
      state.snackbarColor = color;
      state.snackbarIcon = icon;
      state.snackbarMessage = message;
    },
    setAbsoluteBar(state, absoluteBar) {
      state.absoluteBar = absoluteBar;
    },
    setInsertProgressGlobal(state, { absoluteBar, absoluteBarText }) {
      state.absoluteBar = absoluteBar;
      state.absoluteBarText = absoluteBarText;
    },
    setMinimized(state, minimized) {
      state.minimized = minimized;
    },
    setBreadcrumbs(state, payload) {
      state.breadcrumbs = payload.data;
    },
    setIsExport(state, isExport) {
      state.isExport = isExport;
    },
    setStateVariable(state, { payload, variable }) {
      state[variable] = payload;
    },
    setIntervalExport(state, payload) {
      state.intervalExport = payload;
    },
    setExportStatusRequest(state, payload) {
      state.exportStatusRequest = payload;
    },
    setCheckFileRequest(state, payload) {
      state.checkFileRequest = payload;
    },
    setCancelation(state, payload) {
      state.requestData = payload;
    },
    setNotifications(state, { payload }) {
      state.notifications = payload.data;
    },
    clearNotif(state) {
      state.notifications = [];
    },

    setExportData(state, payload) {
      state.exportId = payload.export_id;
      state.countDataExport = payload.count_data;
    },
    setChangeEmailData(state, { token, accessKey, userId, userIdPlain, aesKey }) {
      state.changeEmailToken = token;
      state.changeEmailAccessKey = accessKey;
      state.changeEmailUserId = userId;
      state.changeEmailUserIdPlain = userIdPlain;
      state.changeEmailAesKey = aesKey;
    },
    clearChangeEmailData(state) {
      state.changeEmailToken = null;
      state.changeEmailAccessKey = null;
      state.changeEmailUserId = null;
      state.changeEmailUserIdPlain = null;
      state.changeEmailAesKey = null;
    },
    setSetPasswordData(state, { token, accessKey, userId, userIdPlain, aesKey, email, emailPlain }) {
      state.setPasswordToken = token;
      state.setPasswordAccessKey = accessKey;
      state.setPasswordUserId = userId;
      state.setPasswordUserIdPlain = userIdPlain;
      state.setPasswordAesKey = aesKey;
      state.setPasswordEmail = email;
      state.setPasswordEmailPlain = emailPlain;
    },
    clearSetPasswordData(state) {
      state.setPasswordToken = null;
      state.setPasswordAccessKey = null;
      state.setPasswordUserId = null;
      state.setPasswordUserIdPlain = null;
      state.setPasswordAesKey = null;
      state.setPasswordEmail = null;
      state.setPasswordEmailPlain = null;
    },
    setDormantReactivatedData(state, { token, accessKey, userId, userIdPlain, aesKey }) {
      state.dormantReactivatedToken = token;
      state.dormantReactivatedAccessKey = accessKey;
      state.dormantReactivatedUserId = userId;
      state.dormantReactivatedUserIdPlain = userIdPlain;
      state.dormantReactivatedAesKey = aesKey;
    },
    clearDormantReactivatedData(state) {
      state.dormantReactivatedToken = null;
      state.dormantReactivatedAccessKey = null;
      state.dormantReactivatedUserId = null;
      state.dormantReactivatedUserIdPlain = null;
      state.dormantReactivatedAesKey = null;
    },
  },

  actions: {
    async renderMenu(ctx) {
      ctx.commit('setMenuLoading', true);
      return ctx
        .dispatch('rbac/fetchUserMenu')
        .then((menu_response) => {
          if (menu_response) {
            ctx.commit('setMenus', menu_response);
            return true;
          }
          return false;
        })
        .finally(() => {
          ctx.commit('setMenuLoading', false);
        });
    },
    checkAuth(ctx, { data, key }) {
      if (data == null || key == null || isEmpty(JSON.parse(data).token)) {
        ctx.dispatch('logout');
        localStorage.removeItem('logged_in_qram');
        localStorage.removeItem('qram_key');
        ctx.commit('setAuth', false);
      } else {
        ctx.commit('setAuth', true);
        if (
          ctx.state.user != null &&
          ctx.state.user.username !=
            JSON.parse(localStorage.getItem('logged_in_qram')).user.username
        ) {
          const userData = JSON.parse(localStorage.getItem('logged_in_qram')).user;
          const decryptedUserData = decryptUserData(userData);
          
          ctx.commit('setUser', decryptedUserData);
          
          if (!decryptedUserData.force_password_change && decryptedUserData.force_last_change_password !== 1) {
            ctx.dispatch('renderMenu');
          }
        }
      }
    },
    async requestAuthKey(ctx, { encrypted_request }) {
      ctx.commit('setLoading', true);
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/get-access`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data: {
          x_access_key: encrypted_request,
        },
      };

      const failed = (message) => {
        showAlert(ctx, message, 'mdi-alert', 'red');
      };

      return axios(config)
        .then((response) => {
          if (response.data.code != '200') {
            if (localStorage.getItem('language_qram') == null) {
              failed(response.data.message_id);
            } else {
              failed(response.data.message_en);
            }
            return false;
          }
          return response.data.data.access_key;
        })
        .catch(() => false)
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },
    async registerWebauthn(ctx, { id }) {
      const urlApi = `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/webauthn/register&id=${id}`;
      const config = {
        method: 'post',
        url: urlApi,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
      };
      return axios(config)
        .then((response) => {
          if (response?.data?.code === 200) {
            return response.data.data.publicKey;
          }

          return false;
        });
    },

    async validateRegisterWebauthn (ctx, { credential, id }) {
      const urlApi = `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/webauthn/register/validate&id=${id}`;

      const config = {
        method: 'post',
        url: urlApi,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data: credential
  
      };
      return axios(config)
        .then((response) => {
          if (response?.data?.code === 200) {
            showAlert(ctx, 'YubiKey successfully registered.', 'mdi-alert', 'green');
            return true;
          }
          if (response?.data?.code === 302) {
            showAlert(ctx, response?.data?.data || 'WebAuthn validation failed.', 'mdi-alert', 'red');
            return false;
          }
          showAlert(ctx, 'WebAuthn validation failed.', 'mdi-alert', 'red');
          return false;
        });
    },

    async loginWebauthn(ctx, { id }) {
      const urlApi = `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/webauthn/login&id=${id}`;
      const config = {
        method: 'post',
        url: urlApi,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
      };
      return axios(config)
        .then((response) => {
          if (response?.data?.code === 200) {
            return response.data.data.publicKey;
          }

          return false;
        });
    },

    async validateLoginWebauthn (ctx, { credential, id, handler }) {
      const urlApi = `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/webauthn/login/validate&id=${id}&handler=${handler}`;
      const config = {
        method: 'post',
        url: urlApi,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data: credential
      };
      return axios(config)
        .then((response) => {
          if (response?.data?.code === 200) {
            return response?.data;
          }
          showAlert(ctx, 'WebAuthn validation failed.', 'mdi-alert', 'red');
          return false;
        });
    },
    async resendOtp (ctx) {
      const urlApi = `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/resend-otp`;
      const loginCredential = JSON.parse(JSON.stringify(ctx.state.loginCredential));
      const userCredential = JSON.parse(JSON.stringify(ctx.state.userCredential));

      const speEncrypt = new CustomEncryption();
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3,
      });

      const stringUserId = String(userCredential.userId);

      const safeUserId = stringUserId ? String(stringUserId) : '';
      
      
      const encryptedUserId = speEncrypt.encrypt(
        speEncrypt.encryption(safeUserId, false),
        '',
        extras
      );

      const data = {
        ...loginCredential,
        id_user: encryptedUserId,
        version: btoa(
          CryptoJS.SHA256(
            safeUserId +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      };
      const config = {
        method: 'post',
        url: urlApi,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };
      return axios(config)
        .then((response) => {
          if (response?.data?.code === 200) {
            return response?.data;
          }
          if (response?.data?.code === 314 || response?.data?.code === 409) {
            return response?.data;
          }
          let errorMsg = 'OTP Validation Failed';
          if (typeof response?.data?.data === 'string') {
            errorMsg = response.data.data;
          } else if (response?.data?.message_en) {
            errorMsg = response.data.message_en;
          }
        
          showAlert(ctx, errorMsg, 'mdi-alert', 'red');
          return false;
        })
        .catch((error) => {
          if (error.response?.data) {
            const responseData = error.response.data;
            if (responseData.code === 314 || responseData.code === 409) {
              return responseData;
            }
            let errorMsg = 'Failed to resend OTP.';
            if (typeof responseData.data === 'string') {
              errorMsg = responseData.data;
            } else if (responseData.message_en) {
              errorMsg = responseData.message_en;
            }
            showAlert(ctx, errorMsg, 'mdi-alert', 'red');
            return false;
          }
          // eslint-disable-next-line no-console
          console.error("Resend OTP Error:", error);
          showAlert(ctx, 'Server error during OTP resend.', 'mdi-alert', 'red');
          return false;
        });
    },
    async validateOtp (ctx, { stringOtp }) {
      const urlApi = `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/validate-otp`;
      const loginCredential = JSON.parse(JSON.stringify(ctx.state.loginCredential));
      const userCredential = JSON.parse(JSON.stringify(ctx.state.userCredential));

      const speEncrypt = new CustomEncryption();
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3,
      });

      const stringUserId = String(userCredential.userId);

      const safeOtpCode = stringOtp ? String(stringOtp) : '';
      const safeUserId = stringUserId ? String(stringUserId) : '';
      
      const encryptedOtp = speEncrypt.encrypt(
        speEncrypt.encryption(safeOtpCode, false),
        '',
        extras
      );
      
      const encryptedUserId = speEncrypt.encrypt(
        speEncrypt.encryption(safeUserId, false),
        '',
        extras
      );

      const data = {
        ...loginCredential,
        id_user: encryptedUserId,
        otp_code: encryptedOtp,
        version: btoa(
          CryptoJS.SHA256(
            safeUserId +
            safeOtpCode +
            userCredential.username +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      };

      const visitorId = await generateVisitorId();

      const config = {
        method: 'post',
        url: urlApi,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname,
          'visitor-id': visitorId,
        },
        data,
      };
      return axios(config)
        .then((response) => {
          if (response?.data?.code === 200) {
            return response?.data;
          }
          let errorMsg = 'OTP Validation Failed';
          if (response?.data?.data) {
            errorMsg = response.data.data;
          } else if (response?.data?.message_en) {
            errorMsg = response.data.message_en;
          }

          if (response?.data?.code === 313) {
            return response?.data;
          }
        
          showAlert(ctx, errorMsg, 'mdi-alert', 'red');
          return false;
        })
        .catch((error) => {
          console.error("OTP Validation Error:", error);
          showAlert(ctx, 'Server error during OTP validation.', 'mdi-alert', 'red');
          throw error;
        });
    },

    async login(
      ctx,
      { username, password, encrypted_username : usernameEncrypt, encrypted_password: passwordEncrypt, access_key: accessKey, captchaToken }
    ) {
      ctx.commit('setLoading', true);
      const loginCredential = {
        username: usernameEncrypt,
        access_key: accessKey,
      };
      
      ctx.commit('setLoginCredential',loginCredential );
 
      const data = JSON.stringify({
        username: usernameEncrypt,
        password: passwordEncrypt,
        access_key: accessKey,
        captcha_token: captchaToken,
        version: btoa(
          CryptoJS.SHA256(
            username +
              password +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const visitorId = await generateVisitorId();

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/sign-in`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname,
          'visitor-id': visitorId,
        },
        data,
      };
      const failed = (message) => {
        showAlert(ctx, message, 'mdi-alert', 'red');
      };
      return axios(config)
        .then((response) => {
          const responseData = response?.data || {};
          const { code, message_en: messageEn, message_id: messageId } = responseData;
          let res = false;

          switch (code) {
            case 200: {
              res = JSON.stringify(responseData);
              break;
            }

            case 202: {
              res = JSON.stringify(responseData);

              ctx.commit('setExpiredOtp', {
                expired_time: responseData?.data?.expired_time,
                request_time: responseData?.data?.request_time,
                email: responseData?.data?.email || ''
              });

              const userId = String(responseData?.data?.id || '');
              ctx.commit('setUserCredential', { username, userId });
              break;
            }

            case 205:
            case 206: {
              res = JSON.stringify(responseData);
              break;
            }

            default: {
              const language = localStorage.getItem('language_qram');
              const message = language === null ? messageEn : messageId;
              failed(message);
            }
          }
          
          return res;
        })
        .catch((error) => {
          Sentry.captureException(error); 
          if (error.response) {
            const message =
              localStorage.getItem('language_qram') == null
                ? error.response.data?.message_en || 'An error occurred.'
                : error.response.data?.message_id || 'Terjadi kesalahan.' ;
            failed(message);
          } else {
            failed('Connection failed or the server is not responding.');
          }
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },
    set_login(ctx, { payload }) {
      const forceKey = process.env.VUE_APP_FORCE_SECRET || '';

      if (!isEmpty(payload.token)) {
        const userDataForStorage = { ...payload };
        if (userDataForStorage.force_password_change !== undefined) {
          userDataForStorage.force_password_change = encryptAES256GCM(
            userDataForStorage.force_password_change.toString(), 
            forceKey
          );
        }
        if (userDataForStorage.force_last_change_password !== undefined) {
          userDataForStorage.force_last_change_password = encryptAES256GCM(
            userDataForStorage.force_last_change_password.toString(), 
            forceKey
          );
        }
        localStorage.setItem(
          'logged_in_qram',
          JSON.stringify({ token: payload.token, user: userDataForStorage })
        );
        Sentry.setUser({
          id: payload.id,
          email: payload.email,
          username: payload.username || payload.name,
        });

        ctx.commit('setUser', payload);
        ctx.commit('setAuth', true);

        if (!payload.force_password_change && payload.force_last_change_password != 1) {
          ctx.dispatch('renderMenu');
          if (localStorage.getItem('logged_in_qram') != null && this.state.user && !this.state.user.force_password_change) {
            setupZohoChat(this.state.user);
          }
          ctx.dispatch('getNotif', { date });
        }
      } else {
        ctx.dispatch('logout');
        ctx.commit('setAuth', false);
      }
    },
    async logout(ctx) {
      ctx.commit('setLogoutLoading', true);
      const user_data = JSON.parse(localStorage.getItem('logged_in_qram'));
      if (user_data != null) {
        const config = {
          method: 'post',
          url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/logout`,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': user_data.token,
            'X-Origin-Path': window.location.pathname
          },
          data: '',
        };
        await axios(config)
          .then((response) => {
            if (response.data.code != '200') {
              if (response.data.code == '401' || response.data.code == '403') {
                localStorage.removeItem('logged_in_qram');
                localStorage.removeItem('qram_key');
                ctx.commit('setAuth', false);
              }
            } else {
              localStorage.removeItem('logged_in_qram');
              localStorage.removeItem('qram_key');
              ctx.commit('setAuth', false);
            }
          })
          .catch((error) => {
            if (error.response.status == '403') {
              showAlert(ctx, error.response.data.message, 'mdi-alert', 'red');
            } else if (error.response.status == '401') {
              localStorage.removeItem('logged_in_qram');
              localStorage.removeItem('qram_key');
              ctx.commit('setAuth', false);
            }
          })
          .finally(() => {
            Sentry.setUser(null);
            removeZohoChat();
            ctx.commit('setLogoutLoading', false);
            ctx.commit('inputer/merchant/setDownloadFailedURL', '');
            ctx.commit('inputer/aggregator/setDownloadFailedURL', '');
          });
      } else {
        localStorage.removeItem('logged_in_qram');
        localStorage.removeItem('qram_key');
        ctx.commit('setAuth', false);
        ctx.commit('setLogoutLoading', false);
      }
    },

    async forgotPassword(ctx, { email, encrypted_email, access_key }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        email: encrypted_email,
        access_key,
        version: btoa(
          CryptoJS.SHA256(
            email + date + atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/forgot-password`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      const failed = () => {
        showAlert(
          ctx,
          'Failed to request forgot password !',
          'mdi-alert',
          'red'
        );
      };

      return axios(config)
        .then((response) => {
          let res = false;
          if (response.data.code === 200) {
            res = JSON.stringify(response.data.data);
            showAlert(
              ctx,
              'Reset password sent to your email !',
              'mdi-check-circle',
              'green'
            );
          } else failed();
          return res;
        })
        .catch(() => {
          failed();
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },
    async validateForgotPassword(ctx, { access_key, id, id_encrypted }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        id: id_encrypted,
        access_key,
        version: btoa(
          CryptoJS.SHA256(
            id + date + atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/validate-forgot-password`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      const failed = () => {
        showAlert(ctx, 'Invalid Reset Token !', 'mdi-alert', 'red');
      };

      return axios(config)
        .then((response) => {
          let res = false;
          if (response.data.code === 200) {
            res = JSON.stringify(response.data.data);
          } else failed();
          return res;
        })
        .catch(() => {
          failed();
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },

    async changePassword(
      ctx,
      {
        accessKey,
        id,
        idEncrypted,
        password,
        passwordConfirmation,
        encryptedPassword,
        encryptedPasswordConfirmation,
      }
    ) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        id: idEncrypted,
        access_key: accessKey,
        password: encryptedPassword,
        confirm_password: encryptedPasswordConfirmation,
        version: btoa(
          CryptoJS.SHA256(
            id +
              password +
              passwordConfirmation +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/change-password`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      const failed = (message) => {
        showAlert(ctx, message, 'mdi-alert', 'red');
      };

      return axios(config)
        .then((response) => {
          let res = false;
          if (response.data.code === 200) {
            res = JSON.stringify(response.data.data);
            showAlert(ctx, 'Password changed !', 'mdi-check-circle', 'green');
          } else failed(response.data.message_en);
          return res;
        })
        .catch(() => {
          failed();
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },

    async changeForcePassword(
      ctx,
      {
        password,
        passwordConfirmation,
        passwordCurrent,
        encryptedPassword,
        encryptedPasswordConfirmation,
        encryptedPasswordCurrent,
      }
    ) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        password: encryptedPassword,
        confirm_password: encryptedPasswordConfirmation,
        current_password: encryptedPasswordCurrent,
        version: btoa(
          CryptoJS.SHA256(
            passwordCurrent +
              password +
              passwordConfirmation +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/force-change-password`,
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      const failed = (message) => {
        showAlert(ctx, message, 'mdi-alert', 'red');
      };

      return axios(config)
        .then((response) => {
          let res = false;
          if (response.data.code === 200) {
            res = JSON.stringify(response.data.data);
            showAlert(ctx, 'Password changed !', 'mdi-check-circle', 'green');
          } else {
            const message =
              response.data.code == '302'
                ? response.data.data
                : response.data.message_en;
            failed(message);
          }
          return res;
        })
        .catch(() => {
          failed();
          return false;
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },

    async validateChangeEmailToken(ctx, { token }) {
      ctx.commit('setLoading', true);
      // eslint-disable-next-line new-cap
      const speEncrypt = new CustomEncryption();
      const dataExtraString3Value = dataExtraString3;
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3Value,
      });

      try {
        const decodedToken = atob(token);
        
        const [keyEncryptedRsa, idEncryptedRsa] = decodedToken.split(':');

        const aesKeyFromDecrypt = speEncrypt.decrypt(keyEncryptedRsa, false, false, extras);
        
        const aesEncryptedId = speEncrypt.decrypt(idEncryptedRsa, false, false, extras);
        
        const userIdDecrypted = decryptAES256GCM(aesEncryptedId, aesKeyFromDecrypt);
        
        const version = generateVersionHash(userIdDecrypted.toString(), date);
        
        const accessKeyEncrypted = speEncrypt.encrypt(aesKeyFromDecrypt, '', extras);
        
        const userIdAesEncrypted = encryptAES256GCM(userIdDecrypted.toString(), aesKeyFromDecrypt);
        const userIdRsaEncrypted = speEncrypt.encrypt(userIdAesEncrypted, '', extras);
        
        const data = JSON.stringify({
          id: userIdRsaEncrypted,
          access_key: accessKeyEncrypted,
          version,
        });

        const config = {
          method: 'post',
          url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/change-email-validate`,
          headers: {
            'Content-Type': 'application/json',
            'X-Origin-Path': window.location.pathname
          },
          data,
        };

        return axios(config)
          .then((response) => {
            const { code, data: responseData } = response.data;
            
            if (code === 200) {
              ctx.commit('setChangeEmailData', {
                token,
                accessKey: responseData.access_key,
                userId: userIdRsaEncrypted,
                userIdPlain: userIdDecrypted.toString(),
                aesKey: aesKeyFromDecrypt,
              });
              return JSON.stringify(responseData);
            }
            if (code === 419) {
              showAlert(ctx, responseData || 'Token expired!', 'mdi-alert', 'red');
              return { expired: true, code: 419 };
            }
            
            showAlert(ctx, 'Invalid verification token!', 'mdi-alert', 'red');
            return false;
          })
          .catch((error) => {
            console.error('Error validating change email token:', error);
            const statusCode = error.response?.status;
            const errorData = error.response?.data;
            
            if (statusCode === 500 || errorData?.code === 419) {
              showAlert(ctx, errorData?.data || 'Token expired!', 'mdi-alert', 'red');
              return { expired: true, code: 419 };
            }
            
            showAlert(ctx, 'Invalid verification token!', 'mdi-alert', 'red');
            return false;
          })
          .finally(() => {
            ctx.commit('setLoading', false);
          });
      } catch (error) {
        console.error('Error processing change email token:', error);
        showAlert(ctx, 'Error processing verification token!', 'mdi-alert', 'red');
        ctx.commit('setLoading', false);
        return false;
      }
    },

    async submitChangeEmail(ctx, { new_email }) {
      ctx.commit('setLoading', true);
      const speEncrypt = new CustomEncryption();
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3,
      });

      const { changeEmailAccessKey, changeEmailUserId, changeEmailUserIdPlain, changeEmailAesKey } = ctx.state;

      const emailAesEncrypted = encryptAES256GCM(new_email, changeEmailAesKey);
      const emailEncrypted = speEncrypt.encrypt(emailAesEncrypted, '', extras);

      const version = generateVersionHash(changeEmailUserIdPlain + new_email, date);

      const data = JSON.stringify({
        id: changeEmailUserId,
        email: emailEncrypted,
        access_key: changeEmailAccessKey,
        version,
      });

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/change-email`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      return axios(config)
        .then((response) => {
          const { code, data: responseData } = response.data;

          if (code === 200) {
            showAlert(
              ctx,
              'Email berhasil diubah! Link verifikasi telah dikirim ke email baru',
              'mdi-check-circle',
              'green'
            );
            ctx.commit('clearChangeEmailData');
            return response.data;
          }
          if (code === 419) {
            showAlert(ctx, responseData || 'Token kadaluarsa!', 'mdi-alert', 'red');
            return { expired: true, code: 419 };
          }
          if (code === 302) {
            showAlert(ctx, responseData || 'Email sudah terdaftar!', 'mdi-alert', 'red');
            return response.data;
          }

          showAlert(ctx, 'Gagal mengubah email!', 'mdi-alert', 'red');
          return response.data;
        })
        .catch((error) => {
          const statusCode = error.response?.status;
          const errorData = error.response?.data;

          if (statusCode === 500 || errorData?.code === 419) {
            showAlert(ctx, errorData?.data || 'Token kadaluarsa!', 'mdi-alert', 'red');
            return { expired: true, code: 419 };
          }

          const message = errorData?.data || error.message || 'Gagal mengubah email!';
          showAlert(ctx, message, 'mdi-alert', 'red');
          return { code: errorData?.code || statusCode || 500 };
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },

    async validateSetPasswordToken(ctx, { token }) {
      ctx.commit('setLoading', true);
      // eslint-disable-next-line new-cap
      const speEncrypt = new CustomEncryption();
      const dataExtraString3Value = dataExtraString3;
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3Value,
      });

      try {
        const decodedToken = atob(token);
        
        const [keyEncryptedRsa, idEncryptedRsa, emailEncryptedRsa] = decodedToken.split(':');

        const aesKeyFromDecrypt = speEncrypt.decrypt(keyEncryptedRsa, false, false, extras);
        
        const aesEncryptedId = speEncrypt.decrypt(idEncryptedRsa, false, false, extras);
        const userIdDecrypted = decryptAES256GCM(aesEncryptedId, aesKeyFromDecrypt);
        
        const aesEncryptedEmail = speEncrypt.decrypt(emailEncryptedRsa, false, false, extras);
        const emailDecrypted = decryptAES256GCM(aesEncryptedEmail, aesKeyFromDecrypt);
        
        const version = generateVersionHash(userIdDecrypted.toString() + emailDecrypted.toString(), date);
        
        const accessKeyEncrypted = speEncrypt.encrypt(aesKeyFromDecrypt, '', extras);
        
        const userIdAesEncrypted = encryptAES256GCM(userIdDecrypted.toString(), aesKeyFromDecrypt);
        const userIdRsaEncrypted = speEncrypt.encrypt(userIdAesEncrypted, '', extras);
        
        const emailAesEncrypted = encryptAES256GCM(emailDecrypted.toString(), aesKeyFromDecrypt);
        const emailRsaEncrypted = speEncrypt.encrypt(emailAesEncrypted, '', extras);
        
        const data = JSON.stringify({
          id: userIdRsaEncrypted,
          email: emailRsaEncrypted,
          access_key: accessKeyEncrypted,
          version,
        });

        const config = {
          method: 'post',
          url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/change-email-pwd-validate`,
          headers: {
            'Content-Type': 'application/json',
            'X-Origin-Path': window.location.pathname
          },
          data,
        };

        return axios(config)
          .then((response) => {
            const { code, data: responseData } = response.data;
            
            if (code === 200) {
              ctx.commit('setSetPasswordData', {
                token,
                accessKey: responseData.access_key,
                userId: userIdRsaEncrypted,
                userIdPlain: userIdDecrypted.toString(),
                aesKey: aesKeyFromDecrypt,
                email: emailRsaEncrypted,
                emailPlain: emailDecrypted.toString(),
              });
              return JSON.stringify(responseData);
            }
            if (code === 419) {
              showAlert(ctx, responseData || 'Token expired!', 'mdi-alert', 'red');
              return { expired: true, code: 419 };
            }
            
            showAlert(ctx, 'Invalid verification token!', 'mdi-alert', 'red');
            return false;
          })
          .catch((error) => {
            console.error('Error validating set password token:', error);
            const statusCode = error.response?.status;
            const errorData = error.response?.data;
            
            if (statusCode === 500 || errorData?.code === 419) {
              showAlert(ctx, errorData?.data || 'Token expired!', 'mdi-alert', 'red');
              return { expired: true, code: 419 };
            }
            
            showAlert(ctx, 'Invalid verification token!', 'mdi-alert', 'red');
            return false;
          })
          .finally(() => {
            ctx.commit('setLoading', false);
          });
      } catch (error) {
        console.error('Error processing set password token:', error);
        showAlert(ctx, 'Error processing verification token!', 'mdi-alert', 'red');
        ctx.commit('setLoading', false);
        return false;
      }
    },

    async submitSetPassword(ctx, { password, confirm_password }) {
      ctx.commit('setLoading', true);
      const speEncrypt = new CustomEncryption();
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3,
      });

      const { setPasswordAccessKey, setPasswordUserId, setPasswordUserIdPlain, setPasswordAesKey, setPasswordEmail, setPasswordEmailPlain } = ctx.state;

      const passwordAesEncrypted = encryptAES256GCM(password, setPasswordAesKey);
      const passwordEncrypted = speEncrypt.encrypt(passwordAesEncrypted, '', extras);

      const confirmPasswordAesEncrypted = encryptAES256GCM(confirm_password, setPasswordAesKey);
      const confirmPasswordEncrypted = speEncrypt.encrypt(confirmPasswordAesEncrypted, '', extras);

      const version = generateVersionHash(setPasswordUserIdPlain + setPasswordEmailPlain + password, date);

      const data = JSON.stringify({
        id: setPasswordUserId,
        email: setPasswordEmail,
        password: passwordEncrypted,
        confirm_password: confirmPasswordEncrypted,
        access_key: setPasswordAccessKey,
        version,
      });

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/change-email-pwd`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      return axios(config)
        .then((response) => {
          const { code, message_en, data: responseData } = response.data;

          if (code === 200) {
            showAlert(
              ctx,
              'Password changed successfully! Please login with your new password',
              'mdi-check-circle',
              'green'
            );
            ctx.commit('clearSetPasswordData');
            return response.data;
          }
          if (code === 419) {
            showAlert(ctx, responseData || message_en || 'Token expired!', 'mdi-alert', 'red');
            return { expired: true, code: 419 };
          }
          if (code === 401) {
            showAlert(ctx, responseData || message_en || 'Invalid password!', 'mdi-alert', 'red');
            return response.data;
          }

          showAlert(ctx, responseData || message_en || 'Failed to change password!', 'mdi-alert', 'red');
          return response.data;
        })
        .catch((error) => {
          const statusCode = error.response?.status;
          const errorData = error.response?.data;

          if (statusCode === 500 || errorData?.code === 419) {
            showAlert(ctx, errorData?.data || errorData?.message_en || 'Token expired!', 'mdi-alert', 'red');
            return { expired: true, code: 419 };
          }

          const message = errorData?.data || errorData?.message_en || error.message || 'Failed to change password!';
          showAlert(ctx, message, 'mdi-alert', 'red');
          return { code: errorData?.code || statusCode || 500 };
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },

    async validateDormantReactivatedToken(ctx, { token }) {
      ctx.commit('setLoading', true);
      // eslint-disable-next-line new-cap
      const speEncrypt = new CustomEncryption();
      const dataExtraString3Value = dataExtraString3;
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3Value,
      });

      try {
        const decodedToken = atob(token);
        
        const [keyEncryptedRsa, idEncryptedRsa] = decodedToken.split(':');

        const aesKeyFromDecrypt = speEncrypt.decrypt(keyEncryptedRsa, false, false, extras);
        
        const aesEncryptedId = speEncrypt.decrypt(idEncryptedRsa, false, false, extras);
        const userIdDecrypted = decryptAES256GCM(aesEncryptedId, aesKeyFromDecrypt);
        
        const version = generateVersionHash(userIdDecrypted.toString(), date);
        
        const accessKeyEncrypted = speEncrypt.encrypt(aesKeyFromDecrypt, '', extras);
        
        const userIdAesEncrypted = encryptAES256GCM(userIdDecrypted.toString(), aesKeyFromDecrypt);
        const userIdRsaEncrypted = speEncrypt.encrypt(userIdAesEncrypted, '', extras);
        
        const data = JSON.stringify({
          id: userIdRsaEncrypted,
          access_key: accessKeyEncrypted,
          version,
        });

        const config = {
          method: 'post',
          url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/dormant-reactivated-validate`,
          headers: {
            'Content-Type': 'application/json',
            'X-Origin-Path': window.location.pathname
          },
          data,
        };

        return axios(config)
          .then((response) => {
            const { code, data: responseData } = response.data;
            
            if (code === 200) {
              ctx.commit('setDormantReactivatedData', {
                token,
                accessKey: responseData.access_key,
                userId: userIdRsaEncrypted,
                userIdPlain: userIdDecrypted.toString(),
                aesKey: aesKeyFromDecrypt,
              });
              return JSON.stringify(responseData);
            }
            if (code === 419) {
              showAlert(ctx, responseData || 'Token expired!', 'mdi-alert', 'red');
              return { expired: true, code: 419 };
            }
            
            showAlert(ctx, 'Invalid verification token!', 'mdi-alert', 'red');
            return false;
          })
          .catch((error) => {
            console.error('Error validating dormant reactivated token:', error);
            const statusCode = error.response?.status;
            const errorData = error.response?.data;
            
            if (statusCode === 500 || errorData?.code === 419) {
              showAlert(ctx, errorData?.data || 'Token expired!', 'mdi-alert', 'red');
              return { expired: true, code: 419 };
            }
            
            showAlert(ctx, 'Invalid verification token!', 'mdi-alert', 'red');
            return false;
          })
          .finally(() => {
            ctx.commit('setLoading', false);
          });
      } catch (error) {
        console.error('Error processing dormant reactivated token:', error);
        showAlert(ctx, 'Error processing verification token!', 'mdi-alert', 'red');
        ctx.commit('setLoading', false);
        return false;
      }
    },

    async submitDormantReactivated(ctx, { password, confirm_password }) {
      ctx.commit('setLoading', true);
      const speEncrypt = new CustomEncryption();
      const extras = speEncrypt.decryption({
        enc: process.env.VUE_APP_STRING_EXTRA2,
        masterkey: dataExtraString3,
      });

      const { dormantReactivatedAccessKey, dormantReactivatedUserId, dormantReactivatedUserIdPlain, dormantReactivatedAesKey } = ctx.state;

      const passwordAesEncrypted = encryptAES256GCM(password, dormantReactivatedAesKey);
      const passwordEncrypted = speEncrypt.encrypt(passwordAesEncrypted, '', extras);

      const confirmPasswordAesEncrypted = encryptAES256GCM(confirm_password, dormantReactivatedAesKey);
      const confirmPasswordEncrypted = speEncrypt.encrypt(confirmPasswordAesEncrypted, '', extras);

      const version = generateVersionHash(dormantReactivatedUserIdPlain + password, date);

      const data = JSON.stringify({
        id: dormantReactivatedUserId,
        password: passwordEncrypted,
        confirm_password: confirmPasswordEncrypted,
        access_key: dormantReactivatedAccessKey,
        version,
      });

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/user/auth/dormant-reactivated`,
        headers: {
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      return axios(config)
        .then((response) => {
          const { code, message_en, data: responseData } = response.data;

          if (code === 200) {
            showAlert(
              ctx,
              'Account reactivated successfully! Please login with your new password',
              'mdi-check-circle',
              'green'
            );
            ctx.commit('clearDormantReactivatedData');
            return response.data;
          }
          if (code === 419) {
            showAlert(ctx, responseData || message_en || 'Token expired!', 'mdi-alert', 'red');
            return { expired: true, code: 419 };
          }
          if (code === 401) {
            showAlert(ctx, responseData || message_en || 'Invalid password!', 'mdi-alert', 'red');
            return response.data;
          }

          showAlert(ctx, responseData || message_en || 'Failed to reactivate account!', 'mdi-alert', 'red');
          return response.data;
        })
        .catch((error) => {
          const statusCode = error.response?.status;
          const errorData = error.response?.data;

          if (statusCode === 500 || errorData?.code === 419) {
            showAlert(ctx, errorData?.data || errorData?.message_en || 'Token expired!', 'mdi-alert', 'red');
            return { expired: true, code: 419 };
          }

          const message = errorData?.data || errorData?.message_en || error.message || 'Failed to reactivate account!';
          showAlert(ctx, message, 'mdi-alert', 'red');
          return { code: errorData?.code || statusCode || 500 };
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    },

    async exportList(
      ctx,
      {
        // eslint-disable-next-line no-shadow
        date,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        search,
        exportUrl,
        exportController,
        exportType = null,
      }
    ) {
      if (ctx.state.isExport === true) {
        showAlert(ctx, 'Export in progress !', 'mdi-alert', 'red');

        return;
      }

      ctx.commit('setLoading', true);
      ctx.commit('setIsExport', true);
      ctx.commit('setStateVariable', {
        payload: 0,
        variable: 'countSameExport',
      });
      const sort = !sortDesc[0] ? 'ASC' : 'DESC';
      let dataSortBy = '';
      if (sortBy[0] != null) {
        dataSortBy = `${sortBy[0]}  ${sort}`;
      } else {
        dataSortBy = `id ${sort}`;
      }

      const data = JSON.stringify({
        search,
        sort_by: dataSortBy,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        version: btoa(
          CryptoJS.SHA256(
            page.toString() +
              itemsPerPage.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const failed = () => {
        showAlert(ctx, 'Export Failed !', 'mdi-alert', 'red');

        ctx.commit('setInsertProgressGlobal', {
          absoluteBar: false,
          absoluteBarText: '',
        });
        ctx.commit('setIsExport', false);
      };

      const urlApi = `/index.php?r=${exportUrl}`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData?.code === 200) {
        if (responseData.data.message != null) {
          showAlert(ctx, responseData.data.message, 'mdi-alert', 'red');
          ctx.commit('setIsExport', false);
          return;
        }
        ctx.commit('setExportData', responseData.data);
        const interval = setInterval(() => {
          ctx.dispatch('exportStatus', {
            date,
            exportId: ctx.state.exportId,
            countDataExport: ctx.state.countDataExport,
            controller: exportController,
            // eslint-disable-next-line object-shorthand
            exportType: exportType,
          });
        }, dataIntervalExport);
        ctx.commit('setIntervalExport', interval);
        ctx.commit('setInsertProgressGlobal', {
          absoluteBar: true,
          absoluteBarText: 'Start to export data..',
        });
      } else {
        failed();
      }
    },
    async exportStatus(
      ctx,
      // eslint-disable-next-line no-shadow
      { date, exportId, countDataExport, controller, exportType }
    ) {
      if (ctx.state.exportStatusRequest) ctx.state.exportStatusRequest.cancel();

      const axiosSource = axios.CancelToken.source();
      ctx.commit('setExportStatusRequest', {
        cancel: axiosSource.cancel,
        msg: 'Loading...',
      });

      const data = JSON.stringify({
        export_type: exportType,
        export_id: exportId,
        count_data: countDataExport,
        version: btoa(
          CryptoJS.SHA256(
            exportId.toString() +
              countDataExport.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      try {
        const urlApi = `/index.php?r=api/v1/export/${controller}/status-export`;
        const addConfig = { cancelToken: axiosSource.token };

        const responseData = await postApi(ctx, urlApi, data, addConfig);

        if (responseData?.code == '10') {
          ctx.dispatch('exportExcelCountDefault');
          clearInterval(ctx.state.intervalExport);
          if (dataWaitFileGenerate) {
            ctx.commit(
              'setInsertProgressGlobal',
              {
                absoluteBar: true,
                absoluteBarText: 'Generating File ....',
              },
              { root: true }
            );
            const intervalCheckFile = setInterval(() => {
              ctx.dispatch(
                'exportFileStatus',
                {
                  date,
                  exportId,
                  downloadLink: responseData.data.link_download,
                },
                { root: true }
              );
              ctx.commit(
                'setStateVariable',
                {
                  payload: ctx.state.checkStatusFileCount + 1,
                  variable: 'checkStatusFileCount',
                },
                { root: true }
              );
            }, dataIntervalCheckFile);
            ctx.commit(
              'setStateVariable',
              {
                payload: intervalCheckFile,
                variable: 'intervalCheckFile',
              },
              { root: true }
            );
          } else {
            showAlert(ctx, 'Export Done !', 'mdi-check-circle', 'green');
            ctx.commit(
              'setInsertProgressGlobal',
              {
                absoluteBar: false,
                absoluteBarText: '',
              },
              { root: true }
            );
            ctx.commit('setIsExport', false, { root: true });
            window.location.href = responseData.data.link_download;
          }
        } else if (responseData?.code == '302') {
          ctx.dispatch('exportExcelFailed');
        } else {
          const totalDataDone = parseInt(responseData.data.count_success, 10);
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: true,
              absoluteBarText: `Export ${totalDataDone}/${countDataExport} data..`,
            },
            { root: true }
          );
          if (ctx.state.countDoneExport != totalDataDone) {
            ctx.commit('setStateVariable', {
              payload: totalDataDone,
              variable: 'countDoneExport',
            });
            ctx.commit('setStateVariable', {
              payload: 0,
              variable: 'countSameExport',
            });
          } else {
            ctx.commit('setStateVariable', {
              payload: ctx.state.countSameExport + 1,
              variable: 'countSameExport',
            });
          }
          if (ctx.state.countSameExport > dataMaxExportStatusInterval) {
            ctx.dispatch('exportExcelFailed');
          }
        }
      } catch (error) {
        ctx.dispatch('exportExcelFailed');
      }
    },

    // eslint-disable-next-line no-shadow
    async exportFileStatus(ctx, { date, exportId, downloadLink }) {
      if (ctx.state.checkFileRequest) ctx.state.checkFileRequest.cancel();

      const axiosSource = axios.CancelToken.source();
      ctx.commit('setCheckFileRequest', {
        cancel: axiosSource.cancel,
        msg: 'Loading...',
      });

      const data = JSON.stringify({
        export_id: exportId,
        link_download: downloadLink,
        version: btoa(
          CryptoJS.SHA256(
            exportId.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const failed = () => {
        clearInterval(ctx.state.intervalCheckFile);
        showAlert(ctx, 'Generate File Export Failed !', 'mdi-alert', 'red');
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: '',
          },
          { root: true }
        );
        ctx.commit(
          'setStateVariable',
          {
            payload: 0,
            variable: 'checkStatusFileCount',
          },
          { root: true }
        );
        ctx.commit('setIsExport', false, { root: true });
      };

      let config = {};
      try {
        config = {
          method: 'post',
          url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/export/check-file-exist/index`,
          headers: {
            'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
              .token,
            'Content-Type': 'application/json',
            'X-Origin-Path': window.location.pathname
          },
          data,
          cancelToken: axiosSource.token,
        };
      } catch (err) {
        failed();
      }

      if (ctx.state.checkStatusFileCount > dataMaxCheckFileInterval) {
        failed();
        return;
      }

      await axios(config)
        .then((response) => {
          if (response.data.data == 'done') {
            clearInterval(ctx.state.intervalCheckFile);
            ctx.commit(
              'setStateVariable',
              {
                payload: 0,
                variable: 'checkStatusFileCount',
              },
              { root: true }
            );
            showAlert(ctx, 'Export Done !', 'mdi-check-circle', 'green');
            ctx.commit(
              'setInsertProgressGlobal',
              {
                absoluteBar: false,
                absoluteBarText: '',
              },
              { root: true }
            );
            ctx.commit('setIsExport', false, { root: true });
            window.location.href = downloadLink;
          } else if (response.data.data == 'failed') {
            failed();
          }
        })
        .catch((error) => {
          if (error.response != undefined) {
            failed();
            if (error.response.status == '403') {
              showAlert(ctx, error.response.data.message, 'mdi-alert', 'red');
            } else if (error.response.status == '401') {
              ctx.dispatch('logout', null, { root: true });
            }
          }
        });
    },

    // eslint-disable-next-line no-shadow
    async getVersion(ctx, { date }) {
      const data = JSON.stringify({
        version: btoa(
          CryptoJS.SHA256(
            date + atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/version/version-app/index`,
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      return axios(config).then((response) => {
        let res = false;
        if (response.data.code === 200) {
          res = response.data.data.portal_admin;
        }
        return res;
      });
    },

    // eslint-disable-next-line no-shadow
    async getNotif(ctx, { date }) {
      const cancel = ctx.state.requestData?.data?.cancel;
      if (typeof cancel === 'function') {
        cancel();
      }


      const axiosSource = axios.CancelToken.source();
      ctx.commit('setCancelation', {
        cancel: axiosSource.cancel,
        msg: 'Loading...',
      });

      ctx.commit('clearNotif');
      ctx.commit('setLoading', true);

      const data = JSON.stringify({
        version: btoa(
          CryptoJS.SHA256(
            date + atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const urlApi = `/index.php?r=api/v1/notifikasi/notifikasi-tugas/index`;

      const addConfig = { cancelToken: axiosSource.token };

      const responseData = await postApi(ctx, urlApi, data, addConfig);

      if (responseData?.code === 200) {
        ctx.commit('setNotifications', { payload: responseData });
      }
    },

    async exportExcel(
      ctx,
      {
        // eslint-disable-next-line no-shadow
        date,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        search,
        exportUrl,
        exportController,
        module,
        countData,
        sourceData,
      }
    ) {
      if (ctx.state.isExport === true) {
        showAlert(ctx, 'Export in progress !', 'mdi-alert', 'red');

        return;
      }

      ctx.commit('setLoading', true);
      ctx.commit('setIsExport', true);
      
      const payload = {
        module,
        search,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        count_data: countData,
        source_data: sourceData,
        version: btoa(
          CryptoJS.SHA256(
            module + date + atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      };

      if (sortBy && sortBy.length > 0 && sortDesc && sortDesc.length > 0) {
        const sort = !sortDesc[0] ? 'ASC' : 'DESC';
        let dataSortBy = '';
        if (sortBy[0] != null) {
          dataSortBy = `${sortBy[0]}  ${sort}`;
        } else {
          dataSortBy = `id ${sort}`;
        }
        payload.sort_by = dataSortBy;
      }

      const data = JSON.stringify(payload);

      const urlApi = `/index.php?r=api/v1/${exportUrl}`;

      try {
        const responseData = await postApi(ctx, urlApi, data);

        if (responseData?.code === '00') {
          if (responseData.data.message != null) {
            showAlert(ctx, responseData.data.message, 'mdi-alert', 'red');
            ctx.commit('setIsExport', false);
            return;
          }
          ctx.commit('setExportData', responseData.data);
          const interval = setInterval(() => {
            ctx.dispatch('exportExcelStatus', {
              exportId: ctx.state.exportId,
              module,
              date,
              controller: exportController,
            });
          }, dataIntervalExportCh);
          ctx.commit('setIntervalExport', interval);
          ctx.commit('setInsertProgressGlobal', {
            absoluteBar: true,
            absoluteBarText: 'Start to export data..',
          });
        } else {
          const message =
            responseData?.message_en ||
            responseData?.message_id ||
            (typeof responseData?.data === 'string' && responseData.data) ||
            'Export Failed !';
          showAlert(ctx, message, 'mdi-alert', 'red');
          ctx.commit('setLoading', false);
          ctx.commit('setIsExport', false);
        }
      } catch (error) {
        const errorMsg = error?.message_en || error?.message_id || error?.message || 'Export Failed !';
        showAlert(ctx, errorMsg, 'mdi-alert', 'red');
        ctx.commit('setLoading', false);
        ctx.commit('setIsExport', false);
      }

      
    },

    async exportExcelStatus(
      ctx,
      // eslint-disable-next-line no-shadow
      { exportId, module, date, controller }
    ) {
      if (ctx.state.exportStatusRequest) ctx.state.exportStatusRequest.cancel();

      const axiosSource = axios.CancelToken.source();
      ctx.commit('setExportStatusRequest', {
        cancel: axiosSource.cancel,
        msg: 'Loading...',
      });

      const data = JSON.stringify({
        module,
        export_id: exportId,
        version: btoa(
          CryptoJS.SHA256(
            exportId +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      try {
        const urlApi = `/index.php?r=api/v1/${controller}`;
        const addConfig = { cancelToken: axiosSource.token };

        const responseData = await postApi(ctx, urlApi, data, addConfig);

        if (responseData?.code == '00') {
          clearInterval(ctx.state.intervalExport);
          showAlert(ctx, 'Export Done !', 'mdi-check-circle', 'green');
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: false,
              absoluteBarText: '',
            },
            { root: true }
          );
          ctx.commit('setIsExport', false, { root: true });
          window.location.href = responseData.data.link_file;
        } else if (responseData?.code == 'A0') {
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: true,
              absoluteBarText: 'Preparing to download..',
            },
            { root: true }
          );
        } else {
          ctx.dispatch('exportExcelFailed');
        }
      } catch (error) {
        ctx.dispatch('exportExcelFailed');
      }
    },

    async exportExcelFailed(ctx, message) {
      clearInterval(ctx.state.intervalExport);
      showAlert(ctx, message || 'Export Failed !', 'mdi-alert', 'red');

      ctx.commit(
        'setInsertProgressGlobal',
        {
          absoluteBar: false,
          absoluteBarText: '',
        },
        { root: true }
      );
      ctx.commit('setIsExport', false, { root: true });
      ctx.dispatch('exportExcelCountDefault');
    },

    async exportExcelCountDefault(ctx) {
      ctx.commit('setStateVariable', {
        payload: 0,
        variable: 'countSameExport',
      });
      ctx.commit('setStateVariable', {
        payload: 0,
        variable: 'countDoneExport',
      });
    },
  },

  modules: {
    users,
    rbac,
    daftarTugas,
    register,
    master,
    rekening,
    settingFee,
    feeManagement,
    client,
    report,
    trackingPengajuan,
    inputerTrackingPengajuan,
    inputer,
    dashboard,
    broadcast,
    changePIC,
    resendEmail,
    merchantManagement,
    maintenanceSettlement,
    reportEdc,
    reportWalletTransactionStore,
    nnsManagement,
    settlementSetting,
    dashboardDetail,
    dashboardProfiling,
    fileSettlement,
    monitoringMerchant,
    reportBpjstk,
    promo,
    reportPromo,
    userMerchant,
    forceActiveMerchant,
    reportCpmTransaction,
    stockMid,
    mid,
    reconciliation,
    refundInternal,
    merchantCallbackManagement,
    ...internalRefund,
    ...merchantTools,
    ...insertTransactionOnUs,
    ...insertTransactionOffUs,
    ...systemLog,
    ...shared,
    ...masterData
  },
});
